import { RefObject, useEffect, useRef, useState } from 'react';

type PageElementsMap = Map<number, HTMLDivElement>;

const PAGE_VISIBILITY_THRESHOLD_PERCENTAGE = 0.5;
const PROGRAMMATIC_SCROLL_DELAY_MS = 1000;

export function useCurrentPage(
  containerRef: RefObject<HTMLDivElement | null>,
  pageElements: PageElementsMap,
) {
  const [currentPage, setCurrentPage] = useState(1);
  const isScrollingProgrammatically = useRef(false);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || pageElements.size === 0) return;

    const observer = createPageVisibilityObserver(
      container,
      pageElements,
      isScrollingProgrammatically,
      setCurrentPage,
    );

    observeAllPages(observer, pageElements);

    return () => observer.disconnect();
  }, [containerRef, pageElements]);

  const scrollToPage = (pageNumber: number) => {
    const pageElement = pageElements.get(pageNumber);
    if (!pageElement) return;

    setCurrentPage(pageNumber);
    disableObserverTemporarily(isScrollingProgrammatically);

    pageElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return { currentPage, scrollToPage };
}

function createPageVisibilityObserver(
  container: HTMLDivElement,
  pageElements: PageElementsMap,
  isScrollingProgrammatically: RefObject<boolean>,
  setCurrentPage: (page: number) => void,
): IntersectionObserver {
  const options: IntersectionObserverInit = {
    root: container.parentElement,
    rootMargin: '0px',
    threshold: PAGE_VISIBILITY_THRESHOLD_PERCENTAGE,
  };

  const callback: IntersectionObserverCallback = (entries) => {
    if (isScrollingProgrammatically.current) return;

    const mostVisiblePage = findMostVisiblePage(entries, pageElements);
    if (mostVisiblePage) {
      setCurrentPage(mostVisiblePage);
    }
  };

  return new IntersectionObserver(callback, options);
}

function findMostVisiblePage(
  entries: IntersectionObserverEntry[],
  pageElements: PageElementsMap,
): number | null {
  let maxVisiblePage = 1;
  let maxIntersectionRatio = 0;

  entries.forEach((entry) => {
    if (
      entry.isIntersecting &&
      entry.intersectionRatio > maxIntersectionRatio
    ) {
      const pageNumber = findPageNumber(
        entry.target as HTMLDivElement,
        pageElements,
      );
      if (pageNumber) {
        maxVisiblePage = pageNumber;
        maxIntersectionRatio = entry.intersectionRatio;
      }
    }
  });

  return maxIntersectionRatio > 0 ? maxVisiblePage : null;
}

function findPageNumber(
  element: HTMLDivElement,
  pageElements: PageElementsMap,
): number | null {
  for (const [pageNumber, pageElement] of pageElements.entries()) {
    if (pageElement === element) {
      return pageNumber;
    }
  }
  return null;
}

function observeAllPages(
  observer: IntersectionObserver,
  pageElements: PageElementsMap,
): void {
  pageElements.forEach((element) => observer.observe(element));
}

function disableObserverTemporarily(
  isScrollingProgrammatically: RefObject<boolean>,
): void {
  isScrollingProgrammatically.current = true;

  setTimeout(() => {
    isScrollingProgrammatically.current = false;
  }, PROGRAMMATIC_SCROLL_DELAY_MS);
}
